/** Styling shared by the compact and full chip renderings. */
import type { CSSProperties } from 'react';

import { CANCELLED_COLOR, CANCELLED_TINT, isCancelled } from './cancelled-workshops';
import { DISABLED_COLOR, DISABLED_LABEL, isDisabled } from './disabled-workshops';
import type { WorkshopChip } from './workshopsCalendarTypes';

export const MONO: CSSProperties = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum"',
};

export interface ChipColors {
  bg: string;
  border: string;
  shadow: string;
}

const CANCELLED_CHIP: ChipColors = {
  bg: CANCELLED_TINT,
  border: CANCELLED_COLOR,
  shadow: `0 0 0 1px ${CANCELLED_COLOR}33`,
};

/** A cancelled chip strikes its venue name through, so the state never rests on colour alone. */
export function venueNameStyle(w: WorkshopChip): CSSProperties {
  return isCancelled(w) ? { textDecoration: 'line-through' } : {};
}

/** The coloured state line above the venue name, for a workshop that is not
 *  simply on sale. */
export function chipStateLine(w: WorkshopChip): { label: string; color: string } | null {
  if (isCancelled(w)) return { label: 'Cancelled', color: CANCELLED_COLOR };
  if (isDisabled(w)) return { label: DISABLED_LABEL, color: DISABLED_COLOR };
  return null;
}

/** A disabled chip keeps its region colour but dashes its left edge, so it reads
 *  apart from an on-sale workshop even on the one-line Month chip. */
export function chipEdgeStyle(w: WorkshopChip): CSSProperties['borderLeftStyle'] {
  return isDisabled(w) ? 'dashed' : 'solid';
}

export function chipColors(w: WorkshopChip): ChipColors {
  if (isCancelled(w)) return CANCELLED_CHIP;
  if (w.mine) {
    return {
      bg: 'var(--nb-primary-soft)',
      border: 'var(--nb-primary)',
      shadow: '0 1px 2px rgba(14,135,75,.16), 0 0 0 1px var(--nb-primary)',
    };
  }
  return {
    bg: `${w.accentColor}14`,
    border: w.accentColor,
    shadow: `0 0 0 1px ${w.accentColor}33`,
  };
}

/** Colours for a workshop the viewer can't open (`InertWorkshopChip`) — a
 *  near-white fill and the accent at 55% opacity, per the design's inert-chip
 *  spec. The chip's own 72% opacity (applied by the caller) does the rest of
 *  the muting, including the text. */
export function inertChipColors(w: WorkshopChip): ChipColors {
  if (isCancelled(w)) return CANCELLED_CHIP;
  return {
    bg: 'var(--nb-surface)',
    border: `${w.accentColor}8c`,
    shadow: `0 0 0 1px ${w.accentColor}33`,
  };
}
